import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { Phone, ArrowRight, CheckCircle2, ShieldCheck, Calculator, IdCard } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGridSkeleton } from "@/components/FyndanuGrid";
import { VehicleBrowser } from "@/components/VehicleBrowser";
import { fyndanuListingsQuery } from "@/lib/vehicles";
import { HUSBIL_BRANDS } from "@/data/vehicle-brands";
import { buildListingHead, type ListingLike } from "@/lib/listing-seo";


const URL_SELF = "https://www.akhusbilfritid.se/husbilar-till-salu";

const FAQ = [
  {
    q: "Vilka husbilar har ni till salu?",
    a: "Vi har alltid ett rullande lager av kontrollerade begagnade husbilar från märken som Rapido, Hymer, Kabe, Adria, Bürstner, Knaus och Dethleffs. Filtrera på märke, pris, årsmodell eller körkortsklass direkt här nedan.",
  },
  {
    q: "Vad ska jag tänka på när jag köper husbil?",
    a: "Kontrollera fukt, gas- och elsystem, servicehistorik och rostangrepp. Titta på köpguiden i vår guidesektion — den täcker allt du bör fråga innan du bestämmer dig. Vi går alltid igenom fordonen med dig personligen före köp.",
  },
  {
    q: "Kan jag byta in min gamla husbil eller husvagn?",
    a: "Ja, vi tar in inbyten kontinuerligt. Ring 070-304 53 44 eller kom förbi Stabsvägen 5 i Kalix så bokar vi en tid för värdering.",
  },
  {
    q: "Erbjuder ni finansiering?",
    a: "Ja, vi samarbetar med etablerade finansbolag. På varje fordonssida hittar du en kalkylator där du kan räkna på månadskostnad direkt.",
  },
  {
    q: "Hur långt kör ni ut husbilar?",
    a: "Vi säljer främst till Norrbotten och norra Sverige, men skickar husbilar över hela landet. Transport ordnas mot kostnad — ring så pratar vi ihop oss.",
  },
];

export const Route = createFileRoute("/husbilar-till-salu")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  // Titel, beskrivning och schema byggs från verkligt lager (antal, prisspann,
  // märken) — samma URL och canonical som tidigare, bara vassare metadata.
  head: ({ loaderData }) =>
    buildListingHead({
      category: "husbil",
      items: (loaderData as { items?: ListingLike[] } | undefined)?.items ?? [],
      url: URL_SELF,
      fiUrl: "https://www.akhusbilfritid.se/fi/asuntoautot",
      faq: FAQ,
      fallbackBrands: HUSBIL_BRANDS.map((b) => b.name),
    }),

  component: HusbilarPage,
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-20" role="alert">Något gick fel: {error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => <div className="container-edge py-20">Sidan hittades inte.</div>,
});

function HusbilarPage() {
  return (
    <>
      <PageHeader
        label="Husbilar · Kontrollerade & prissatta rakt"
        title={<>Husbilar till salu <span className="font-italic-display text-accent">i Kalix</span>.</>}
        sub="Filtrera på märke, pris, årsmodell och körkortsklass. Varje husbil är fukttestad, gasoltestad och prissatt utan dolda avgifter."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/husvagnar-till-salu" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Söker du husvagn? <ArrowRight size={14} />
          </Link>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Värdera inbyte <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="container-edge py-14">
        <Suspense fallback={<FyndanuGridSkeleton />}>
          <VehicleBrowser category="husbil" emptyLabel="Inga husbilar matchar filtret just nu. Prova att rensa filtret eller ring så letar vi." />
        </Suspense>
      </section>

      {/* Märkessidor */}
      <section className="container-edge py-10">
        <p className="eyebrow">Bläddra per märke</p>
        <h2 className="mt-3 font-display text-2xl md:text-4xl">Populära husbilsmärken</h2>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {HUSBIL_BRANDS.map((b) => (
            <Link key={b.slug} to="/husbilar/$brand" params={{ brand: b.slug }} className="group rounded-2xl border border-border bg-card p-4 text-center transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg">
              <p className="font-display text-lg group-hover:text-accent">{b.name}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{b.origin.split(" ")[0]}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Verktyg */}
      <section className="container-edge py-6">
        <div className="grid gap-4 md:grid-cols-2">
          <Link to="/verktyg/manadskostnad-husbil" className="group flex items-center gap-4 rounded-3xl border border-border bg-card p-6 transition hover:border-accent hover:shadow-lg">
            <div className="rounded-full bg-primary/10 p-3 text-accent"><Calculator size={22} /></div>
            <div>
              <p className="font-display text-lg group-hover:text-accent">Räkna månadskostnad</p>
              <p className="text-sm text-muted-foreground">Pris, kontantinsats & ränta → månadsbelopp direkt.</p>
            </div>
            <ArrowRight size={18} className="ml-auto text-accent" />
          </Link>
          <Link to="/verktyg/korkort-husbil" className="group flex items-center gap-4 rounded-3xl border border-border bg-card p-6 transition hover:border-accent hover:shadow-lg">
            <div className="rounded-full bg-primary/10 p-3 text-accent"><IdCard size={22} /></div>
            <div>
              <p className="font-display text-lg group-hover:text-accent">Vilket körkort krävs?</p>
              <p className="text-sm text-muted-foreground">Ange totalvikt → se om B, B96, BE eller C1 krävs.</p>
            </div>
            <ArrowRight size={18} className="ml-auto text-accent" />
          </Link>
        </div>
      </section>


      {/* Trygghetsavsnitt */}
      <section className="bg-secondary py-16">
        <div className="container-edge">
          <p className="eyebrow">Så köper du tryggt hos oss</p>
          <div className="mt-6 grid gap-6 md:grid-cols-4">
            {[
              { i: <ShieldCheck size={20} />, t: "Genomgången", d: "Fukttest, gasoltest och service innan leverans." },
              { i: <CheckCircle2 size={20} />, t: "Raka priser", d: "Ingen dold påslagning — priset är priset." },
              { i: <CheckCircle2 size={20} />, t: "Inbyte välkommet", d: "Vi tar in husbilar och husvagnar kontinuerligt." },
              { i: <CheckCircle2 size={20} />, t: "Transport i hela Sverige", d: "Vi ordnar leverans dörr till dörr." },
            ].map((x) => (
              <div key={x.t} className="rounded-3xl border border-border bg-card p-6">
                <div className="inline-flex rounded-full bg-primary/10 p-2 text-accent">{x.i}</div>
                <h3 className="mt-4 font-display text-xl">{x.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-edge py-16">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl">Att köpa husbil hos AK</h2>
        <div className="mt-8 divide-y divide-border rounded-3xl border border-border bg-card">
          {FAQ.map((f, i) => (
            <details key={i} className="group p-6">
              <summary className="cursor-pointer list-none font-display text-lg">
                {f.q}
              </summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
